// Synchronisation FFBB depuis les appareils (voir ffbb.ts : la FFBB refuse les serveurs).
//
// Synchro d'une équipe par son staff : pour chaque compétition reliée, lire la poule, garder NOS
// rencontres, les confier à ffbb_sync_matches() qui range et met à jour ; un match à venir déplacé ou
// retiré devient une alerte que le coach valide (D-048) ; le score suit la règle de D-113 (score saisi
// au club prioritaire, « pas joué » sans effet).
//
// Une fois par jour au plus et par club (la base tient le compte), la synchro relit d'abord les
// engagements du club : une 2e phase après les brassages, une coupe, des phases finales, une
// catégorie nouvelle sont reliées toutes seules, puis leurs matchs synchronisés (D-090).
//
// L'annuaire des clubs (D-094) : chaque rencontre synchronisée apprend son adversaire FFBB (organisme) ;
// à la fin, les fiches que la base demande (inconnues, ou relues il y a plus d'une semaine) sont lues et
// déposées, logos compris. Une panne de l'annuaire ne gêne jamais la synchro. Staff seulement.
//
// Plus frais (D-113) :
//  A. « Récupérer le score » (fetchScore) : tout membre de l'équipe ; la base dit si une lecture est utile.
//  B. En arrière-plan (scheduleFfbbReads) : à l'ouverture de l'app, les lectures que la base réclame
//     (30 min après 2 h de jeu jusqu'au score, sinon 6 h, rien la nuit, un appareil à la fois par poule),
//     après l'affichage, jamais pendant une saisie.
//  C. Une lecture de poule passe à tous les clubs Arceau de la poule (ffbb_poule_apply) : seulement leurs
//     scores, chacun avec sa règle. Le staff fait en plus la synchro complète de ses compétitions.
//  D. Chaque échec de lecture est signalé à la base (installFfbbHealth) : code HTTP, étape, appareil.
// Une poule = un appel, jamais plus.
import {
  ffbbAttach, ffbbHealthReport, ffbbLinkedTeams, ffbbPouleApply, ffbbReadsClaim, ffbbScanClaim, ffbbScoreClaim, ffbbSetVenues, ffbbSync, teamFfbbLinks,
  type AttachResult, type FfbbLink, type ReadClaim,
} from './api';
import { venueOf } from '../domain/trip';
import { ffbbHallAddresses } from './salles-api';
import { clubEngagements, onFfbbFailure, pouleRencontres } from './ffbb';
import { pouleRows, toSyncRow, type FfbbRencontre, type SyncRow } from '../domain/ffbb';
import { fetchVerdict, readsMayRun, scoreText, type ScoreClaim } from '../domain/score';
import { opponentPairs, type DueOrg } from '../domain/annuaire';
import { directoryLink, readDirectory } from './annuaire';

export interface SyncSummary {
  inserted: number; updated: number; toValidate: number; failed: string[];
  /** D-113 : scores à vérifier (écart avec le score saisi au club, 0-0 FFBB). */
  scoreAlerts?: number;
  /** Compétitions reliées toutes seules (« Trophée … (SF1) ») et équipes créées, D-090. */
  linked: string[]; newTeams: string[];
}
const emptySummary = (): SyncSummary => ({ inserted: 0, updated: 0, toValidate: 0, failed: [], linked: [], newTeams: [] });

/** Une synchro est due si une compétition n'a jamais été lue, ou pas depuis `hours` heures. */
export const syncDue = (links: FfbbLink[], hours = 6) =>
  links.some(l => !l.last_sync_at || Date.now() - new Date(l.last_sync_at).getTime() > hours * 3600_000);

/** Relire les engagements du club si la base le demande (une fois par jour) ; null sinon, ou si la FFBB ne répond pas. */
async function scanClub(clubId: string): Promise<AttachResult | null> {
  try {
    const code = await ffbbScanClaim(clubId);
    if (!code) return null;
    return await ffbbAttach(clubId, await clubEngagements(code));
  } catch { return null; }
}

type LinkLike = Pick<FfbbLink, 'engagement_id' | 'poule_id' | 'competition_name'>;

/** Une compétition du staff, d'après la poule déjà lue : nos rencontres, leurs salles, leurs adversaires (annuaire). */
async function syncLinkWith(raw: FfbbRencontre[], teamId: string, l: LinkLike, sum: SyncSummary, due?: Map<string, DueOrg>): Promise<void> {
  const rows = raw
    .map(r => toSyncRow(r, l.engagement_id))
    .filter((r): r is SyncRow => r !== null);
  const res = await ffbbSync(teamId, l.engagement_id, rows);
  // Les salles de nos rencontres, géolocalisées par la FFBB (trajet des déplacements).
  const ours = new Set(rows.map(r => r.ffbb_id));
  const venues = raw.filter(r => ours.has(String(r.id)))
    .map(r => ({ ffbb_id: String(r.id), v: venueOf(r.salle) })).filter(x => x.v)
    .map(x => ({ ffbb_id: x.ffbb_id, lat: x.v!.lat, lng: x.v!.lng, label: x.v!.label }));
  if (venues.length) await ffbbSetVenues(teamId, venues).catch(() => undefined);
  // D-123 : l'adresse des salles de nos matchs à domicile (la salle elle-même est créée par la base, d'après le lieu).
  const homes = rows.filter(x => x.is_home && x.location).map(x => ({ x, v: venues.find(y => y.ffbb_id === x.ffbb_id), r: raw.find(y => String(y.id) === x.ffbb_id) }))
    .filter(h => h.v).map(h => ({ label: h.x.location!, address: h.r?.salle?.adresse?.trim() || null, lat: h.v!.lat, lng: h.v!.lng }));
  if (homes.length) await ffbbHallAddresses(teamId, homes).catch(() => undefined);
  sum.inserted += res.inserted; sum.updated += res.updated; sum.toValidate = Math.max(sum.toValidate, res.to_validate);
  sum.scoreAlerts = (sum.scoreAlerts ?? 0) + (res.score_alerts ?? 0);
  // L'adversaire de chacune de NOS rencontres (pas le reste de la poule) ; la base dit quelles fiches lire.
  const pairs = opponentPairs(raw, l.engagement_id).filter(p => ours.has(p.ffbb_id));
  if (due && pairs.length) (await directoryLink(teamId, l.engagement_id, pairs).catch(() => null))?.due.forEach(d => due.set(d.org_id, d));
}

/** `reads` : les poules déjà lues pendant cette synchro (une poule = un appel, même pour deux compétitions). */
async function syncLink(teamId: string, l: LinkLike, sum: SyncSummary, due?: Map<string, DueOrg>, reads?: Map<string, FfbbRencontre[]>): Promise<void> {
  try {
    const raw = reads?.get(l.poule_id) ?? await pouleRencontres(l.poule_id);
    reads?.set(l.poule_id, raw);
    await syncLinkWith(raw, teamId, l, sum, due);
  } catch {
    sum.failed.push(l.competition_name);
  }
}

/** C. Les poules lues passent aux autres clubs Arceau de la poule (leurs scores seulement). */
async function sharePoules(reads: Map<string, FfbbRencontre[]>): Promise<number> {
  let changed = 0;
  for (const [poule, raw] of reads) changed += (await ffbbPouleApply(poule, pouleRows(raw)).catch(() => null))?.changed ?? 0;
  return changed;
}

/** `clubId` : relire aussi les engagements du club quand c'est le moment (synchro complète seulement). */
export async function syncTeamFfbb(teamId: string, only?: string, clubId?: string): Promise<SyncSummary> {
  const sum = emptySummary();
  const scan = clubId && !only ? await scanClub(clubId) : null;
  const links = (await teamFfbbLinks(teamId)).filter(l => !only || l.engagement_id === only);
  const due = new Map<string, DueOrg>();
  const reads = new Map<string, FfbbRencontre[]>();
  for (const l of links) await syncLink(teamId, l, sum, due, reads);
  // Les compétitions reliées ailleurs dans le club, là où cet appareil a la main (bureau, responsable
  // technique) : leurs matchs arrivent aussi. Les autres équipes les liront à leur prochaine synchro.
  for (const a of scan?.added ?? []) if (a.team_id !== teamId && a.can_sync) await syncLink(a.team_id, a, sum, due, reads);
  await sharePoules(reads);
  // L'annuaire des clubs : les fiches à (re)lire, toutes adversaires d'un match du club.
  if (due.size) await readDirectory(teamId, [...due.values()]).catch(() => 0);
  sum.linked = (scan?.added ?? []).map(a => `${a.competition_name}${a.squad ? ` (${a.squad})` : ''}`);
  sum.newTeams = (scan?.teams ?? []).map(t => t.name);
  return sum;
}

export function summaryText(s: SyncSummary): string {
  const parts = [
    s.inserted ? `${s.inserted} nouveau${s.inserted > 1 ? 'x' : ''} match${s.inserted > 1 ? 's' : ''}` : '',
    s.updated ? `${s.updated} mis à jour` : '',
    s.toValidate ? `${s.toValidate} changement${s.toValidate > 1 ? 's' : ''} à valider` : '',
    s.scoreAlerts ? `${s.scoreAlerts} score${s.scoreAlerts > 1 ? 's' : ''} à vérifier` : '',
  ].filter(Boolean);
  const linked = s.newTeams.length ? `Nouvelle équipe : ${s.newTeams.join(', ')}. `
    : s.linked.length ? `${s.linked.length > 1 ? 'Nouvelles compétitions reliées' : 'Nouvelle compétition reliée'} : ${s.linked.join(', ')}. ` : '';
  if (s.failed.length) return `${linked}FFBB injoignable pour : ${s.failed.join(', ')}`;
  return linked + (parts.length ? `Calendrier FFBB : ${parts.join(', ')}` : 'Calendrier FFBB à jour');
}

// --- A. « Récupérer le score » --------------------------------------------------------------------------
export interface FetchResult { text: string; done: boolean; changed: boolean; /** « 58 – 49 » une fois le score arrivé. */ score?: string }

/** Tout membre de l'équipe : la base dit si une lecture est utile ; sinon, depuis quand la poule est lue. */
export async function fetchScore(eventId: string): Promise<FetchResult> {
  let claim: ScoreClaim;
  try { claim = await ffbbScoreClaim(eventId); } catch { return { text: 'Impossible pour le moment', done: false, changed: false }; }
  if (claim.state !== 'read') return { ...fetchVerdict(claim), changed: false };
  let raw: FfbbRencontre[];
  try { raw = await pouleRencontres(claim.poule_id); } catch { return { ...fetchVerdict(claim, null), changed: false }; }
  // Le staff fait aussi la synchro complète de ses compétitions de la poule (sans l'annuaire : léger).
  const sum = emptySummary();
  for (const l of claim.staff_links) await syncLinkWith(raw, l.team_id, l, sum).catch(() => undefined);
  const a = await ffbbPouleApply(claim.poule_id, pouleRows(raw), eventId).catch(() => null);
  if (!a) return { text: 'Impossible pour le moment', done: false, changed: false };
  const e = a.event;
  return { ...fetchVerdict(claim, e), changed: a.changed > 0 || sum.inserted + sum.updated > 0,
    score: e?.result_confirmed ? scoreText(e.score_us, e.score_them, e.forfeit) : undefined };
}

// --- B. En arrière-plan, les lectures que la base réclame -------------------------------------------------
let running = false;
let lastRun = 0;
let waiting: ReturnType<typeof setTimeout> | null = null;
let visibilityHooked = false;

/** Les lectures dues, une poule après l'autre (4 au plus). `staffTeams` : les équipes où l'appareil a la main ;
 *  les clubs qui ont déjà une compétition reliée relisent leurs engagements une fois par jour (D-090), comme à
 *  la première synchro du staff : un club qui n'a rien relié n'est jamais relié d'office. Rend le nombre de
 *  scores arrivés. */
export async function runDueReads(staffTeams: { id: string; club_id: string }[]): Promise<number> {
  if (running) return 0;
  running = true; lastRun = Date.now();
  let changed = 0;
  try {
    const sum = emptySummary();
    const due = new Map<string, DueOrg>();
    let dirTeam: string | null = null;
    const linked = new Set(staffTeams.length ? await ffbbLinkedTeams(staffTeams.map(t => t.id)).catch(() => [] as string[]) : []);
    for (const club of new Set(staffTeams.filter(t => linked.has(t.id)).map(t => t.club_id))) {
      const scan = await scanClub(club);
      for (const a of scan?.added ?? []) if (a.can_sync) { await syncLink(a.team_id, a, sum, due); dirTeam ??= a.team_id; }
    }
    const claims = await ffbbReadsClaim(4).catch(() => [] as ReadClaim[]);
    for (const c of claims) {
      const raw = await pouleRencontres(c.poule_id).catch(() => null);
      if (!raw) break;   // la FFBB ne répond pas (échec signalé) : la base relancera plus tard
      for (const l of c.staff_links) { await syncLinkWith(raw, l.team_id, l, sum, due).catch(() => undefined); dirTeam ??= l.team_id; }
      changed += (await ffbbPouleApply(c.poule_id, pouleRows(raw)).catch(() => null))?.changed ?? 0;
    }
    if (due.size && dirTeam) await readDirectory(dirTeam, [...due.values()]).catch(() => 0);
  } finally { running = false; }
  return changed;
}

/** À l'ouverture de l'app, après l'affichage ; au retour sur l'app après 10 minutes. Jamais pendant une
 *  saisie (stats, lien de saisie, champ en cours) : on attend une minute, cinq fois au plus. */
export function scheduleFfbbReads(staffTeams: { id: string; club_id: string }[], delay = 4000): void {
  const go = (tries: number) => {
    waiting = null;
    if (running) return;
    if (!readsMayRun(location.hash, document.activeElement?.tagName, !!document.querySelector('.ls'))) {
      if (tries < 5) waiting = setTimeout(() => go(tries + 1), 60_000);
      return;
    }
    void runDueReads(staffTeams).catch(() => undefined);
  };
  const later = (fn: () => void) => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 5000 }); else fn();
  };
  if (waiting) clearTimeout(waiting);
  waiting = setTimeout(() => later(() => go(0)), delay);
  if (!visibilityHooked) {
    visibilityHooked = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRun > 10 * 60_000) scheduleFfbbReads(staffTeams, 2000);
    });
  }
}

// --- D. Santé de la FFBB : les échecs de cet appareil sont signalés -------------------------------------------
let lastReport = 0;
let sessionDevice: string | null = null;
const randomId = () => (typeof crypto.randomUUID === 'function' ? crypto.randomUUID()
  : Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join(''));

/** Un identifiant d'appareil tiré au hasard, sans lien avec le compte (compter les appareils, rien d'autre). */
function deviceId(): string {
  try {
    let d = localStorage.getItem('arceau.device');
    if (!d || !/^[0-9a-f-]{8,40}$/.test(d)) { d = randomId(); localStorage.setItem('arceau.device', d); }
    return d;
  } catch { return (sessionDevice ??= randomId()); }
}

export function installFfbbHealth(): void {
  onFfbbFailure((status, step) => {
    if (step === 'logo') return;                               // un logo manquant n'est pas un refus
    if (status === 0 && navigator.onLine === false) return;   // hors ligne : pas la faute de la FFBB
    if (Date.now() - lastReport < 120_000) return;            // un signalement toutes les 2 min au plus
    lastReport = Date.now();
    void ffbbHealthReport(status, step, deviceId()).catch(() => undefined);
  });
}
